"use client";

/**
 * @group Utils
 * 
 * @summary
 * <blockquote>React hook for managing AbortController lifecycle with component unmounting. CLIENT-ONLY.</blockquote>
 * 
 * @remarks
 * This hook is for client components only. For server-side usage, use createAbortController() instead.
 * 
 * @example
 * ```typescript
 * import { useAbortController } from '@shared/httpService';
 * 
 * function UserList() {
 *   const abortController = useAbortController();
 *   
 *   useEffect(() => {
 *     fetchUsers(abortController.signal);
 *   }, []);
 *   
 *   // AbortController will be automatically aborted when component unmounts
 * }
 * ```
 */
import { useEffect, useRef } from 'react';

/**
 * @internal
 */
export function useAbortController(): AbortController {
  const controllerRef = useRef<AbortController>(new AbortController());
  
  useEffect(() => {
    const controller = controllerRef.current;
    
    // Cleanup function - abort all pending requests when component unmounts
    return () => {
      controller.abort();
    };
  }, []);
  
  return controllerRef.current;
}
